// BUILD-042 — Equipment & Property: a readable schedule of what has been issued, derived on the server from the structured,
// frozen items of generated equipment checkouts. There is nothing to enter here and nothing to keep in step: the same items
// that were on the checkout form (and its PDF) are listed, with the status of the checkout's document.
//
// What this is NOT: it is not an inventory or custody system. A signature or HR review records acknowledgment of the list;
// it does not prove possession or return, and a cancelled checkout is shown as cancelled, never as returned equipment.
// Returns, offboarding, procurement and payroll deductions are separate, unbuilt work.
import { useState } from 'react';
import { downloadHrDocumentSubmission, downloadHrDocumentTemplate, type HrPropertyItem, type HrPropertyState } from '../../../api/client';
import { formatLocalDate } from '../hrFormat';
import { checkDownloadUrl } from './documentFileCheck';
import { CardTitle } from './SectionHeading';
import type { PropertyLoad } from './usePropertySchedule';
import { EquipmentCheckoutPanel } from './EquipmentCheckoutPanel';
import { useHrAccess } from '../HrAccessContext';
import { DeniedNotice, TargetUnavailableNotice, UnavailableNotice } from './LoadNotices';
import { needsPermissionMessage, PERMISSION_LABEL, useSurfaceFlag } from './loadState';

export const PROPERTY_STATE_LABEL: Record<HrPropertyState, string> = {
  awaiting_signature: 'Awaiting signature',
  signed_awaiting_review: 'Signed — awaiting HR review',
  needs_correction: 'Needs correction — sign again',
  reviewed_complete: 'Reviewed complete',
  cancelled: 'Checkout cancelled',
  history: 'Moved to History',
};
const PROPERTY_STATE_COLOR: Record<HrPropertyState, string> = {
  awaiting_signature: 'text-amber-800 bg-amber-50',
  signed_awaiting_review: 'text-blue-800 bg-blue-50',
  needs_correction: 'text-red-700 bg-red-50',
  reviewed_complete: 'text-green-800 bg-green-50',
  cancelled: 'text-gray-600 bg-gray-100',
  history: 'text-gray-600 bg-gray-100',
};

function ItemRow({ item, mode }: { item: HrPropertyItem; mode: 'self' | 'admin' }) {
  const [notice, setNotice] = useState('');
  async function open(disposition: 'inline' | 'attachment') {
    setNotice(disposition === 'inline' ? 'Checking file…' : 'Preparing download…');
    try {
      const { url, fileName } = item.document.kind === 'signed_copy' && item.document.submissionId
        ? await downloadHrDocumentSubmission(item.document.submissionId, disposition)
        : await downloadHrDocumentTemplate(item.document.assignmentId, disposition);
      const result = await checkDownloadUrl(url);
      if (!result.looksValid) { setNotice(`"${fileName}" could not be opened: ${result.reason}`); return; }
      setNotice('');
      window.open(url, '_blank', 'noopener');
    } catch (e: any) {
      setNotice(e?.message || 'Failed to prepare the file.');
    }
  }
  const doc = item.document;
  return (
    <li className="py-3 grid grid-cols-1 md:grid-cols-12 gap-x-4 gap-y-1 text-sm" data-testid="property-row" data-item-key={item.itemKey} data-state={item.state}>
      <div className="md:col-span-3">
        <div className="font-semibold text-gray-900">{item.equipmentType}</div>
        <div className="text-gray-700">{item.description}</div>
        {item.model && <div className="text-xs text-gray-500">Model: {item.model}</div>}
      </div>
      <div className="md:col-span-2">
        <div className="text-xs text-gray-500">Serial / asset no.</div>
        <div className="text-gray-900">{item.serialOrAsset ?? '—'}</div>
      </div>
      <div className="md:col-span-2">
        <div className="text-xs text-gray-500">Issued</div>
        <div className="text-gray-900">{formatLocalDate(item.issueDate)}</div>
        <div className="text-[11px] text-gray-400">Checkout {item.checkoutId.slice(0, 8)}</div>
      </div>
      <div className="md:col-span-2">
        <div className="text-xs text-gray-500">Notes</div>
        <div className="text-gray-900">{item.notes ?? '—'}</div>
      </div>
      <div className="md:col-span-3 md:text-right">
        <span className={`inline-block text-xs font-medium px-2 py-1 rounded-md ${PROPERTY_STATE_COLOR[item.state]}`} data-testid="property-state">{PROPERTY_STATE_LABEL[item.state]}</span>
        <div className="mt-1 text-xs flex gap-3 md:justify-end items-center flex-wrap" data-testid="property-doc" data-doc-kind={doc.kind}>
          <span className="text-gray-500">{doc.label}:</span>
          {doc.kind === 'unavailable' ? (
            <span className="text-gray-400 italic">Unavailable</span>
          ) : (
            <>
              <button type="button" onClick={() => open('inline')} className="text-[#4AABDE] hover:underline font-medium" data-testid="property-doc-view">View</button>
              <button type="button" onClick={() => open('attachment')} className="text-[#4AABDE] hover:underline font-medium" data-testid="property-doc-download">Download</button>
            </>
          )}
        </div>
        {notice && <div className="mt-1 text-xs text-amber-700">{notice}</div>}
        {mode === 'admin' && item.signedVia === 'paper_upload' && <div className="mt-1 text-[11px] text-gray-400">Signed on paper and uploaded</div>}
      </div>
    </li>
  );
}

export function PropertySection({ load, mode, employeeId, onReload }: { load: PropertyLoad; mode: 'self' | 'admin'; employeeId?: string; onReload?: () => void }) {
  const [otherOpen, setOtherOpen] = useState(false);
  const { state: accessState } = useHrAccess();
  const mayManageDocuments = useSurfaceFlag('mayManageDocuments');
  const callerEmployeeId = accessState.kind === 'ready' ? accessState.me.employeeId : null;
  // Oct 24 (equipment discoverability): HR issues equipment from here too, using the SAME guarded checkout panel as Documents & Signatures (same drafts,
  // same generated form, same assignment: no second store). Shown unless the server has said the caller lacks the document permission; when the flag is
  // unknown (older server) it is shown and the server's answer decides. Returns are NOT recorded here: they stay in Documents & Signatures.
  const issuePanel = mode === 'admin' && employeeId ? (
    mayManageDocuments === false ? (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 mb-4 text-xs text-amber-800" data-testid="property-issue-needs-permission">
        {needsPermissionMessage(PERMISSION_LABEL.documents, 'issue equipment')}
      </div>
    ) : (
      <div data-testid="property-issue-equipment">
        <EquipmentCheckoutPanel employeeId={employeeId} callerEmployeeId={callerEmployeeId} onAssigned={() => onReload?.()} />
        <p className="text-xs text-gray-500 -mt-2 mb-4 px-1" data-testid="property-returns-note">Returns are recorded in Documents &amp; Signatures.</p>
      </div>
    )
  ) : null;
  if (load.status === 'loading') return <>{issuePanel}<div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 text-sm text-gray-400">Loading…</div></>;
  if (load.status === 'error') {
    // Admin mode distinguishes WHY (the schedule is a capability-gated, employee-targeted read); self mode and an unclassified error keep the plain message.
    const detail = mode !== 'admin' || !load.kind ? <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 text-sm text-red-700" role="alert">{load.message}</div>
      : load.kind === 'denied' ? <DeniedNotice thing="the equipment schedule" permission={PERMISSION_LABEL.documents} testId="property-denied" />
      : load.kind === 'targetUnavailable' ? <TargetUnavailableNotice testId="property-target-unavailable" />
      : <UnavailableNotice thing="the equipment schedule" message={load.message} onRetry={onReload} testId="property-unavailable" />;
    return <>{issuePanel}{detail}</>;
  }
  const { schedule } = load;
  const c = schedule.counts;
  return (
    <>
    {issuePanel}
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5" data-testid="property-section">
      <CardTitle>{mode === 'self' ? 'Equipment issued to you' : 'Equipment issued to this employee'}</CardTitle>
      <div className="text-sm text-gray-700 mb-1" data-testid="property-count">
        <span className="font-semibold">{c.onRecord}</span> {c.onRecord === 1 ? 'item' : 'items'} on record
        {c.onRecord > 0 && (
          <span className="text-gray-500">
            {' '}— {c.awaitingSignature} awaiting signature, {c.signedAwaitingReview} signed awaiting HR review, {c.reviewedComplete} reviewed complete
          </span>
        )}
      </div>
      {schedule.items.length === 0 ? (
        <div className="text-sm text-gray-400 py-3" data-testid="property-empty">
          {mode === 'self' ? 'No equipment has been issued to you yet.' : 'No equipment checkouts have been issued to this employee yet.'}
        </div>
      ) : (
        <ul className="divide-y divide-gray-100 mt-2">
          {schedule.items.map((it) => <ItemRow key={it.itemKey} item={it} mode={mode} />)}
        </ul>
      )}

      {schedule.otherItems.length > 0 && (
        <div className="mt-4 border-t border-gray-100 pt-2" data-testid="property-other">
          <button type="button" onClick={() => setOtherOpen((v) => !v)} className="text-xs font-semibold text-gray-500 uppercase tracking-wide hover:text-gray-700" aria-expanded={otherOpen}>
            Cancelled or archived checkouts ({schedule.otherItems.length}) {otherOpen ? '▾' : '▸'}
          </button>
          <div className="text-[11px] text-gray-400 mt-1">These are not counted above. A cancelled checkout is not a record that the equipment was returned.</div>
          {otherOpen && (
            <ul className="divide-y divide-gray-100 mt-2">
              {schedule.otherItems.map((it) => <ItemRow key={it.itemKey} item={it} mode={mode} />)}
            </ul>
          )}
        </div>
      )}

      <details className="text-xs text-gray-500 mt-4">
        <summary className="text-[#4AABDE] cursor-pointer">How this list works</summary>
        <ul className="mt-2 max-w-xl space-y-1.5 list-disc pl-4">
          {schedule.semantics.map((s) => <li key={s}>{s}</li>)}
        </ul>
      </details>
    </div>
    </>
  );
}
